/**
 * useProductCoverImages – batch-fetch cover images from media_assets for a list of product IDs.
 * Resolution: cover → gallery[0] → absent.
 * Returns a Record<productId, coverUrl>.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { resolveAssetUrl } from '@/lib/media/mediaApi';
import type { MediaAsset, VariantWidth } from '@/lib/media/mediaTypes';

export function useProductCoverImages(
  productIds: string[],
  variant: VariantWidth = 320,
) {
  const sortedIds = [...productIds].sort();
  const key = sortedIds.join(',');

  return useQuery<Record<string, string>>({
    queryKey: ['product-cover-images', key, variant],
    queryFn: async () => {
      if (sortedIds.length === 0) return {};

      const { data, error } = await supabase
        .from('media_assets')
        .select('*')
        .eq('owner_type', 'product')
        .in('owner_id', sortedIds)
        .in('kind', ['cover', 'gallery'])
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (error) throw error;

      const result: Record<string, string> = {};
      // Group by owner_id, prefer cover over gallery
      for (const id of sortedIds) {
        if (result[id]) continue;
        const owned = (data ?? []).filter((a) => a.owner_id === id);
        if (owned.length === 0) continue;

        const cover = owned.find((a) => a.kind === 'cover');
        const best = cover ?? owned[0];
        const asset = best as unknown as MediaAsset;

        const url =
          resolveAssetUrl(asset, variant) ??
          resolveAssetUrl(asset, variant === 320 ? 640 : 320);

        if (url) result[id] = url;
      }
      return result;
    },
    enabled: sortedIds.length > 0,
    staleTime: 60_000,
  });
}
